// La card de `agregar_a_lanzamiento` — ADR-2026-09-09-el-agente-edita-lanzamientos.
//
// Dos fuentes, y la segunda es la que motivó todo esto:
//   • Productos del CATÁLOGO (`product_ids`): se suman con su ficha, sin fotos todavía.
//   • Una PLANILLA adjunta: cada fila se convierte en una fila BORRADOR del lanzamiento,
//     sin producto del catálogo detrás
//     (ADR-2026-09-09-productos-de-lanzamiento-sin-catalogo). Después, cuando el producto
//     real llegue con ese SKU, adopta la fila y le caen las fotos encima.
//
// Como la de editar, SIEMPRE espera el click: acá se crean filas que después se publican.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, PackagePlus } from 'lucide-react';
import { filasDePlanilla, type CampoEditable } from './agenteLanzamiento';
import {
  agregarFilasBorrador,
  agregarProductosDelCatalogo,
  lanzamientoParaElAgente,
  MAX_PRODUCTOS_POR_LANZAMIENTO,
  type FilaBorrador,
} from './lanzamientosApi';
import { ultimaPlanillaAdjuntada } from '../v2/adjuntosDelMensaje';
import { useAgentEnv } from '../v2/agentEnvContext';
import { obtenerPlanilla } from '../v2/planillas/planillasEnMemoria';
import type { UseV2ChatReturn, V2ToolCard } from '../v2/useV2Chat';
import { avisarCambioDeLanzamiento } from './cambioDeLanzamiento';

const COLUMNAS_PREVIA = 5;
const FILAS_PREVIA = 6;

type Fase = 'cargando' | 'propuesta' | 'sin_datos' | 'creando' | 'listo' | 'error';

export const AgregarALanzamientoCard: React.FC<{
  card: V2ToolCard;
  active: boolean;
  chat: UseV2ChatReturn;
}> = ({ card, active, chat }) => {
  const { companyId } = useAgentEnv();
  const input = (card.input ?? {}) as {
    lanzamiento_id?: string;
    product_ids?: unknown;
    desde_planilla?: unknown;
    hoja?: unknown;
    mensaje?: string;
  };
  const productIds = useMemo(
    () => (Array.isArray(input.product_ids) ? input.product_ids.map(String).filter(Boolean) : []),
    [input.product_ids],
  );
  const desdePlanilla = input.desde_planilla === true;

  const [fase, setFase] = useState<Fase>('cargando');
  const [lanzamiento, setLanzamiento] = useState<{ id: string; nombre: string } | null>(null);
  const [filas, setFilas] = useState<FilaBorrador[]>([]);
  const [columnas, setColumnas] = useState<CampoEditable[]>([]);
  const [descartadas, setDescartadas] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);
  const [creados, setCreados] = useState(0);
  const arrancoRef = useRef(false);
  const resueltoRef = useRef(false);

  useEffect(() => {
    if (arrancoRef.current || !companyId) return;
    arrancoRef.current = true;
    (async () => {
      const lanz = await lanzamientoParaElAgente(companyId, input.lanzamiento_id);
      if (!lanz) {
        setFase('sin_datos');
        setAviso('No encontré un lanzamiento en borrador.');
        return;
      }
      setLanzamiento({ id: lanz.id, nombre: lanz.nombre });
      if (!desdePlanilla) {
        setFase(productIds.length > 0 ? 'propuesta' : 'sin_datos');
        if (productIds.length === 0) setAviso('No me pasaste qué productos agregar.');
        return;
      }
      const adjunta = ultimaPlanillaAdjuntada(chat.messages);
      const planilla = obtenerPlanilla(adjunta?.planillaId);
      if (!planilla) {
        setFase('sin_datos');
        setAviso(
          adjunta
            ? 'La planilla ya no está disponible (se recargó la página). Pedile que la vuelva a adjuntar.'
            : 'No hay ninguna planilla adjunta.',
        );
        return;
      }
      const nombreHoja = typeof input.hoja === 'string' ? input.hoja.trim().toLowerCase() : '';
      const hoja =
        planilla.hojas.find((h) => h.nombre.toLowerCase() === nombreHoja) ?? planilla.hojas[0];
      const leidas = filasDePlanilla(hoja?.filas ?? []);
      setFilas(leidas.productos.map((p) => p.campos as FilaBorrador));
      setColumnas(Object.keys(leidas.columnas) as CampoEditable[]);
      setDescartadas(leidas.descartadas);
      if (leidas.productos.length === 0) {
        setFase('sin_datos');
        setAviso(
          'No pude reconocer productos en esa planilla: hace falta al menos una columna de nombre o de SKU.',
        );
        return;
      }
      setFase('propuesta');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Sin datos la card no pide un click: se resuelve y el agente pide lo que falta.
  useEffect(() => {
    if (fase !== 'sin_datos' || resueltoRef.current) return;
    resueltoRef.current = true;
    chat.resolveToolCard(card.id, { added: false, motivo: aviso ?? 'sin_datos' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const agregar = async () => {
    if (!active || fase !== 'propuesta' || !lanzamiento || !companyId || resueltoRef.current) return;
    resueltoRef.current = true;
    setFase('creando');
    try {
      const r = desdePlanilla
        ? await agregarFilasBorrador(lanzamiento.id, companyId, filas)
        : await agregarProductosDelCatalogo(lanzamiento.id, companyId, productIds);
      const cuantos = 'creados' in r ? r.creados : r.agregados;
      setCreados(cuantos);
      if (cuantos > 0) avisarCambioDeLanzamiento(lanzamiento.id);
      setAviso(r.error);
      setFase('listo');
      chat.resolveToolCard(card.id, {
        added: cuantos > 0,
        cantidad: cuantos,
        lanzamiento: lanzamiento.nombre,
        borrador: desdePlanilla,
        ...('yaEstaban' in r && r.yaEstaban > 0 ? { ya_estaban: r.yaEstaban } : {}),
        ...(r.error ? { aviso: r.error } : {}),
      });
    } catch (err) {
      console.warn('[AgregarALanzamiento] crear', err);
      resueltoRef.current = false;
      setAviso('No pude agregarlos. Probá de nuevo.');
      setFase('propuesta');
    }
  };

  const cancelar = () => {
    if (resueltoRef.current) return;
    resueltoRef.current = true;
    setFase('listo');
    chat.resolveToolCard(card.id, { added: false, motivo: 'cancelado' });
  };

  if (fase === 'cargando') {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-[#02062B]/[0.08] bg-white p-4 text-[13.5px] text-[#02062B]/[0.55]">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Preparando…
      </div>
    );
  }

  if (fase === 'sin_datos' || fase === 'error') {
    return (
      <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-4 text-[13.5px] text-[#02062B]/[0.6]">
        {aviso}
      </div>
    );
  }

  if (fase === 'listo') {
    return (
      <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-4">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">Listo</p>
        <p className="mt-1 flex items-center gap-2 text-[14px] text-brand-black">
          {creados > 0 && <Check className="h-4 w-4 text-brand-primary" aria-hidden="true" />}
          {creados > 0
            ? `Sumé ${creados} producto(s) a "${lanzamiento?.nombre}".`
            : 'No se agregó ninguno.'}
        </p>
        {aviso && <p className="mt-1 text-[12.5px] text-[#02062B]/[0.55]">{aviso}</p>}
      </div>
    );
  }

  const total = desdePlanilla ? filas.length : productIds.length;

  return (
    <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-3">
      <div className="flex items-center gap-2 px-1 pb-2">
        <PackagePlus className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
        <span className="min-w-0">
          <span className="block truncate text-[14px] font-medium text-brand-black">
            {lanzamiento?.nombre}
          </span>
          <span className="block text-[12px] text-[#02062B]/[0.55]">
            {total} producto(s) {desdePlanilla ? 'desde la planilla' : 'del catálogo'}
          </span>
        </span>
      </div>

      {input.mensaje && (
        <p className="px-1 pb-2 text-[13px] text-[#02062B]/[0.7]">{input.mensaje}</p>
      )}

      {desdePlanilla && (
        <>
          <div className="overflow-x-auto rounded-xl border border-[#02062B]/[0.06]">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="bg-[#02062B]/[0.03]">
                  {columnas.slice(0, COLUMNAS_PREVIA).map((c) => (
                    <th key={c} className="px-2 py-1.5 text-left font-medium text-brand-black">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.slice(0, FILAS_PREVIA).map((f, i) => (
                  <tr key={i} className="border-t border-[#02062B]/[0.05]">
                    {columnas.slice(0, COLUMNAS_PREVIA).map((c) => (
                      <td
                        key={c}
                        className="max-w-[140px] truncate px-2 py-1 align-top text-brand-black/85"
                      >
                        {(f as Record<string, string | undefined>)[c] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-1 pt-1.5 text-[11.5px] text-[#02062B]/[0.5]">
            {filas.length > FILAS_PREVIA ? `+${filas.length - FILAS_PREVIA} fila(s) más. ` : ''}
            {descartadas > 0 ? `${descartadas} fila(s) sin nombre ni SKU quedaron afuera. ` : ''}
            Entran sin fotos: cuando mandes el producto con el mismo SKU, se le suman ahí.
          </p>
        </>
      )}

      {total > MAX_PRODUCTOS_POR_LANZAMIENTO && (
        <p className="mt-2 flex items-start gap-1.5 px-1 text-[12px] text-[#02062B]/[0.55]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#C9971C]" aria-hidden="true" />
          Un lanzamiento admite hasta {MAX_PRODUCTOS_POR_LANZAMIENTO} productos: van a entrar
          los primeros.
        </p>
      )}

      {aviso && <p className="mt-2 px-1 text-[12px] text-[#C33B36]">{aviso}</p>}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={cancelar}
          disabled={!active}
          className="rounded-xl py-2 px-4 text-[13px] font-medium text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.06] disabled:opacity-40"
        >
          Ahora no
        </button>
        <button
          type="button"
          onClick={() => void agregar()}
          disabled={!active || (fase as Fase) === 'creando'}
          className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary py-2 px-4 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
        >
          {(fase as Fase) === 'creando' && (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          )}
          Agregar al lanzamiento
        </button>
      </div>
    </div>
  );
};

export default AgregarALanzamientoCard;
